import {
  AfterContentInit,
  Component,
  ComponentRef,
  Inject,
  InjectionToken,
  OnInit,
  Type,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Observable, combineLatest} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {selectCurrentCatalogId} from '../../common/catalog/catalog.selectors';
import {
  selectHasRegistrationCancellationRecommendation,
  selectOrganizationName,
} from '../../common/context-data/context-data.selectors';
import {
  OnDestroySubject,
  ProductComponent,
  ProductComponentModel,
  ProductType,
  serviceOfferComponentKey,
  ysoProductComponentKey,
} from '../../data/model';
import * as addonSelectors from '../reducers';
import {ServiceOfferComponent} from '../service-offer/service-offer.component';
import {RenderTargetDirective} from './directives/renderer-target.directive';
import {YsoProductsPageComponent} from '../yso-products/yso-products-page.component';

export type ProductComponentMap = {[type: string]: Type<ProductComponent>};
/**
 * A map for determining the type of product component to render.
 */
export const COMPONENT_MAP = new InjectionToken<ProductComponentMap>(
  'Product to component map',
  {
    providedIn: 'root',
    factory: (): ProductComponentMap => {
      return {
        [serviceOfferComponentKey]: ServiceOfferComponent,
        [ysoProductComponentKey]: YsoProductsPageComponent,
      };
    },
  },
);

@Component({
  selector: 'app-addon-renderer',
  templateUrl: './add-on-renderer.component.html',
  styleUrls: ['./addon-renderer.component.scss'],
  host: {class: 'app-addon-renderer'},
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddonRendererComponent
  extends OnDestroySubject
  implements AfterContentInit, OnInit
{
  @ViewChild(RenderTargetDirective, {static: true})
  addonHost!: RenderTargetDirective;
  addon$: Observable<ProductComponentModel<ProductType> | undefined>;
  showRecommendation$: Observable<boolean>;
  orgName$: Observable<string | undefined>;
  private componentRef: ComponentRef<ProductComponent>;

  constructor(
    @Inject(COMPONENT_MAP)
    private productComponentMap: ProductComponentMap,
    private store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.addon$ = this.store.pipe(
      select(addonSelectors.selectAddonByCatalogId),
      takeUntil(this.onDestroy),
    );
    const currentCatalogId$ = this.store.pipe(select(selectCurrentCatalogId));
    const hasRecommendation$ = this.store.pipe(
      select(selectHasRegistrationCancellationRecommendation),
    );
    this.orgName$ = this.store.pipe(select(selectOrganizationName));

    this.showRecommendation$ = combineLatest([
      currentCatalogId$,
      hasRecommendation$,
    ]).pipe(
      map(([catalogId, hasRec]) => {
        const target = 'verticalinsure-registrationcancellation-catalog';
        return !!hasRec && catalogId?.toLowerCase?.() === target;
      }),
      takeUntil(this.onDestroy),
    );
  }

  ngAfterContentInit(): void {
    this.store
      .pipe(
        select(addonSelectors.selectAddonByCatalogId),
        takeUntil(this.onDestroy),
      )
      .subscribe(addon => this.renderAddon(addon));
  }

  renderAddon(
    componentModel: ProductComponentModel<ProductType> | undefined,
  ): void {
    if (!componentModel) return;

    const ref = this.addonHost.viewContainerRef;
    ref.clear();

    const componentType: Type<ProductComponent> =
      this.productComponentMap[componentModel.componentKey];

    this.componentRef = ref.createComponent<ProductComponent>(componentType);
    this.componentRef.changeDetectorRef.detectChanges();
  }
}
